<template>
  <div class="librarian-list-page">
    <!-- Sticky Header: 搜索框 和 管理按钮 -->
    <van-sticky :offset-top="0">
      <div class="sticky-header-content">
        <div class="top-bar-with-search">
          <van-field
            v-model="searchKeyword"
            left-icon="search"
            placeholder="搜索姓名或拼音首字母"
            clearable
            @update:model-value="onSearchInput"
            class="inline-search-field"
          />
          <van-button
            icon="setting-o"
            type="default"
            size="small"
            @click="showAdminPanel = true"
            class="inline-manage-button"
          >
            管理
          </van-button>
        </div>
      </div>
    </van-sticky>

    <!-- 列表内容区域 -->
    <div class="scrollable-list-area">
        <p style="text-align: center; padding: var(--van-padding-sm); color: blue;" v-if="showDevMessages">LibrarianList Component Loaded!</p>

        <van-list
          v-model:loading="loadingLibrarians"
          :finished="true"
          :finished-text="sortedLibrarians.length === 0 && searchKeyword ? '没有匹配到结果' : (sortedLibrarians.length === 0 ? '暂无馆员数据' : '没有更多了')"
          v-if="componentInitialized"
          class="librarian-van-list"
        >
          <div v-for="librarian in sortedLibrarians" :key="librarian.id" class="librarian-list-item-wrapper">
            <van-cell class="librarian-item" :clickable="false">
              <template #title>
                <span :style="{ color: librarian.nameColor }" class="librarian-name" @click="toggleDetails(librarian.id)">
                  {{ librarian.name }}
                  <van-icon :name="expandedLibrarianId === librarian.id ? 'arrow-up' : 'arrow-down'" size="16" style="margin-left: 5px;" />
                </span>
                <van-button
                  size="mini"
                  type="default"
                  plain
                  icon="edit"
                  @click.stop="editLibrarian(librarian.id)"
                  class="modify-info-button"
                  v-if="expandedLibrarianId !== librarian.id"
                >
                  修改信息
                </van-button>
                <van-tag v-if="librarian.isDueForFollowUpFromSickness" type="danger" style="margin-left: 8px; padding: 4px 8px;">生病需追访</van-tag>
              </template>
              <template #label>
                <div class="librarian-detail">出生年月: {{ librarian.birthday || '未记录' }}</div>
                <div class="librarian-detail">上次联系: {{ formatTimestamp(librarian.last_contact_timestamp) || '从未联系' }}</div>
                <div class="librarian-detail">
                  当前健康状况: <span :style="{ color: getHealthStatusColor(librarian.health_status), fontWeight: 'bold' }">{{ librarian.health_status || '未记录' }}</span>
                </div>
                <div class="librarian-detail" :style="{ color: librarian.nameColor === 'blue' || librarian.nameColor === 'red' ? librarian.nameColor : 'inherit', fontWeight: (librarian.nameColor === 'blue' || librarian.nameColor === 'red' ? 'bold' : 'normal') }">
                  下次预计联系: {{ librarian.displayNextContactDate }}
                </div>
              </template>
              <template #right-icon>
                <div class="item-actions">
                  <van-button size="small" type="warning" @click.stop="openContactModal(librarian)" class="action-button">新记录</van-button>
                  <!-- 编辑最近一次联系记录按钮 -->
                  <van-button
                    v-if="librarian.last_contact_log_id"
                    size="small"
                    type="default"
                    plain
                    icon="records"
                    @click.stop="openContactModal(librarian, true, librarian.last_contact_log_id)"
                    class="action-button edit-record-button"
                  >
                    修改记录
                  </van-button>
                </div>
              </template>
            </van-cell>
            <div v-if="expandedLibrarianId === librarian.id" class="librarian-details-expanded">
              <div v-if="loadingExpandedLogs" class="expanded-loading"> <van-loading type="spinner" size="20px">加载联系记录...</van-loading> </div>
              <div v-else-if="currentExpandedLogs.length > 0" class="expanded-logs-scrollable">
                <van-cell-group inset class="expanded-details-group">
                  <div v-for="log in currentExpandedLogs" :key="log.id" class="log-item">
                    <van-cell title="联系时间" :value="formatTimestamp(log.contact_timestamp, true)" />
                    <van-cell title="通话时长" :value="log.call_duration_minutes !== null ? `${log.call_duration_minutes} 分钟` : '未记录'" />
                    <van-cell title="联系情况" :value="log.notes || '未记录'" :label="log.notes ? '' : null" class="log-notes"/>
                    <van-cell title="记录时健康状况" :value="log.health_status_recorded || '未记录'" />
                    <van-cell title="联系人" :value="log.contact_person_name || '未知'" />
                  </div>
                </van-cell-group>
              </div>
              <div v-else class="expanded-no-logs"> 暂无历史联系记录。 </div>
              <div style="text-align: center; padding-top: 10px; padding-bottom: 5px;"> <van-button size="mini" type="default" @click="toggleDetails(librarian.id)">收起记录</van-button> </div>
            </div>
          </div>
        </van-list>
        <div v-if="!componentInitialized && !initialLoadingError" style="text-align: center; padding: 20px;"> <van-loading type="spinner" size="24px">加载中...</van-loading> </div>
        <div v-if="initialLoadingError" style="text-align: center; padding: 20px; color: red;"> 加载馆员列表失败，请检查网络或联系管理员。 </div>
    </div>

    <van-popup v-model:show="showContactModal" position="bottom" :style="{ height: 'auto', maxHeight: '80vh' }" round closeable @closed="onContactModalClosed">
      <ContactModal
        v-if="selectedLibrarian"
        :librarian="selectedLibrarian"
        :editing-log="editingContactLogData"
        @submit="handleContactSubmit"
        @close="showContactModal = false"
      />
    </van-popup>

        <van-popup v-model:show="showAdminPanel" position="bottom" :style="{ height: 'auto', maxHeight: '85vh' }" round closeable @open="onAdminPanelOpen">
      <div class="admin-panel">
        <van-nav-bar title="管理设置" />
        <van-tabs v-model:active="activeAdminTab" sticky swipeable>
          <van-tab title="数据管理">
            <div class="admin-tab-content">
              <van-cell-group inset title="馆员数据" class="admin-cell-group">
                <van-cell center title="导入馆员表格"> <!-- 修改文本 -->
                  <template #right-icon>
                    <van-uploader :after-read="handleImport" accept=".csv" :disabled="isUploading" result-type="file">
                      <van-button icon="upgrade" type="primary" size="small" :loading="isUploading" loading-text="导入中...">选择文件</van-button> <!-- 修改文本 -->
                    </van-uploader>
                  </template>
                </van-cell>
                <van-cell center title="添加新馆员" clickable @click="openAddLibrarianDialog"> <!-- 新增按钮 -->
                  <template #right-icon><van-icon name="add-o" /></template>
                </van-cell>
                <van-cell center title="导出联系记录" clickable @click="openExportDialogInAdminPanel" />
              </van-cell-group>
            </div>
          </van-tab>
          <van-tab title="用户管理">
            <div class="admin-tab-content user-management-content">
              <van-button type="primary" icon="plus" @click="openCreateUserDialog" style="margin-bottom: 10px; width: 100%;"> 创建新用户 </van-button>
              <div v-if="loadingUsers" style="text-align:center; padding: 20px;"><van-loading /></div>
              <van-list v-if="!loadingUsers && users.length > 0" :finished="true" finished-text="已显示所有用户">
                <van-cell-group inset title="用户列表">
                  <van-cell v-for="userInList in users" :key="userInList.id">
                    <template #title> <div class="user-title">{{ userInList.username }}</div> <div class="user-label">全名: {{ userInList.full_name || '未设置' }}</div> <div class="user-label">创建于: {{ formatTimestamp(userInList.created_at) }}</div> </template>
                    <template #right-icon> <div class="user-actions"> <van-button size="mini" type="default" @click="openEditUserDialog(userInList)" style="margin-bottom: 5px;">编辑</van-button> <van-button size="mini" type="primary" plain @click="openChangePasswordDialog(userInList)" style="margin-bottom: 5px;">改密</van-button> <van-button size="mini" type="danger" @click="confirmDeleteUser(userInList)" :disabled="isDeletingUser === userInList.id || currentUserId === userInList.id" :loading="isDeletingUser === userInList.id">删除</van-button> </div> </template>
                  </van-cell>
                </van-cell-group>
              </van-list>
              <van-empty v-if="!loadingUsers && users.length === 0" description="暂无其他用户数据" />
            </div>
          </van-tab>
        </van-tabs>
      </div>
    </van-popup>

    <van-dialog
      v-model:show="showCreateUserDialog"
      title="创建新用户"
      show-cancel-button
      confirm-button-text="创建"
      :before-close="beforeCreateUserDialogClose"
      @confirm="handleCreateUser"
      @cancel="resetCreateUserForm"
      :confirm-button-loading="isSubmittingUserForm"
      :close-on-click-overlay="false"
      class="user-form-dialog"
    >
      <van-form ref="createUserFormRef" @submit.prevent="() => {}" class="user-form-padding">
        <van-cell-group inset>
          <van-field v-model="newUserData.username" name="username" label="用户名" placeholder="请输入用户名" :rules="[{ required: true, message: '用户名不能为空' }, { pattern: /^[a-zA-Z0-9_]{3,20}$/, message: '3-20位字母、数字或下划线' }]" clearable />
          <van-field v-model="newUserData.full_name" name="full_name" label="全名" placeholder="请输入用户全名" :rules="[{ required: true, message: '全名不能为空' }]" clearable />
          <van-field v-model="newUserData.password" type="password" name="password" label="密码" placeholder="请输入密码 (至少6位)" :rules="[{ required: true, message: '密码不能为空' }, { validator: passwordValidator, message: '密码至少6位' }]" clearable />
          <van-field v-model="newUserData.confirmPassword" type="password" name="confirmPassword" label="确认密码" placeholder="请再次输入密码" :rules="[{ required: true, message: '请确认密码' }, { validator: confirmPasswordValidator, message: '两次输入的密码不一致' }]" clearable />
        </van-cell-group>
      </van-form>
    </van-dialog>

    <van-dialog
      v-model:show="showEditUserDialog"
      title="编辑用户信息"
      show-cancel-button
      confirm-button-text="保存"
      :before-close="beforeEditUserDialogClose"
      @confirm="handleEditUser"
      @cancel="resetEditUserForm"
      :confirm-button-loading="isSubmittingUserForm"
      :close-on-click-overlay="false"
      class="user-form-dialog"
    >
      <van-form ref="editUserFormRef" @submit.prevent="() => {}" class="user-form-padding">
        <van-cell-group inset>
          <van-field v-model="editingUserData.username" name="edit_username" label="用户名" placeholder="请输入用户名" :rules="[{ required: true, message: '用户名不能为空' }, { pattern: /^[a-zA-Z0-9_]{3,20}$/, message: '3-20位字母、数字或下划线' }]" clearable :readonly="editingUserData.id === currentUserId" />
          <van-field v-model="editingUserData.full_name" name="edit_full_name" label="全名" placeholder="请输入用户全名" :rules="[{ required: true, message: '全名不能为空' }]" clearable />
        </van-cell-group>
      </van-form>
    </van-dialog>

    <van-dialog
      v-model:show="showChangePasswordDialog"
      title="修改密码"
      show-cancel-button
      confirm-button-text="确认修改"
      :before-close="beforeChangePasswordDialogClose"
      @confirm="handleChangePassword"
      @cancel="resetChangePasswordForm"
      :confirm-button-loading="isSubmittingUserForm"
      :close-on-click-overlay="false"
      class="user-form-dialog"
    >
      <van-form ref="changePasswordFormRef" @submit.prevent="() => {}" class="user-form-padding">
        <van-cell-group inset>
          <van-field
            v-model="userForPasswordChange.username"
            name="changePwdUsername"
            label="用户名"
            readonly
          />
          <van-field
            v-if="isEditingSelfPassword"
            v-model="changePasswordData.old_password"
            type="password"
            name="old_password"
            label="旧密码"
            placeholder="请输入旧密码"
            :rules="[{ required: true, message: '请输入旧密码' }]"
            clearable
          />
          <van-field
            v-model="changePasswordData.new_password"
            type="password"
            name="new_password"
            label="新密码"
            placeholder="请输入新密码 (至少6位)"
            :rules="[{ required: true, message: '新密码不能为空' }, { validator: passwordValidator, message: '密码至少6位' }]"
            clearable
          />
          <van-field
            v-model="changePasswordData.confirm_new_password"
            type="password"
            name="confirm_new_password"
            label="确认新密码"
            placeholder="请再次输入新密码"
            :rules="[{ required: true, message: '请确认新密码' }, { validator: val => val === changePasswordData.new_password, message: '两次输入的新密码不一致' }]"
            clearable
          />
        </van-cell-group>
      </van-form>
    </van-dialog>

    <van-dialog v-model:show="showExportDialog" title="导出联系记录" show-cancel-button @confirm="handleExport">
      <van-cell-group inset>
        <van-field
          v-model="exportStartDate"
          is-link
          readonly
          name="startDate"
          label="开始日期"
          placeholder="点击选择日期"
          @click="showStartDatePicker = true"
        />
        <van-field
          v-model="exportEndDate"
          is-link
          readonly
          name="endDate"
          label="结束日期"
          placeholder="点击选择日期"
          @click="showEndDatePicker = true"
        />
      </van-cell-group>
    </van-dialog>
    <van-popup v-model:show="showStartDatePicker" position="bottom">
      <van-date-picker
        type="date"
        :min-date="minDateForPicker"
        :max-date="maxDateForPicker"
        @confirm="onStartDateConfirm"
        @cancel="showStartDatePicker = false"
      />
    </van-popup>
    <van-popup v-model:show="showEndDatePicker" position="bottom">
      <van-date-picker
        type="date"
        :min-date="minDateForPicker"
        :max-date="maxDateForPicker"
        @confirm="onEndDateConfirm"
        @cancel="showEndDatePicker = false"
      />
    </van-popup>

    <!-- 新增：添加单个馆员对话框 -->
    <van-dialog
      v-model:show="showAddLibrarianDialog"
      title="添加新馆员"
      show-cancel-button
      confirm-button-text="添加"
      :before-close="beforeAddLibrarianDialogClose"
      @confirm="handleAddLibrarian"
      @cancel="resetAddLibrarianForm"
      :confirm-button-loading="isSubmittingLibrarianForm"
      :close-on-click-overlay="false"
      class="user-form-dialog"
    >
      <van-form ref="addLibrarianFormRef" @submit.prevent="() => {}" class="user-form-padding">
        <van-cell-group inset>
          <van-field
            v-model="newLibrarianData.name"
            name="newLibrarianName"
            label="姓名"
            placeholder="请输入馆员姓名"
            :rules="[{ required: true, message: '姓名不能为空' }]"
            clearable
          />
          <van-field
            v-model="newLibrarianData.birthday"
            name="newLibrarianBirthday"
            label="出生年月"
            placeholder="格式 YYYY.M 或 YYYY.MM"
            :rules="[{ pattern: /^\d{4}\.(0?[1-9]|1[0-2])$/, message: '格式 YYYY.M 或 YYYY.MM' }]"
            clearable
            maxlength="7"
          />
          <van-field
            v-model="newLibrarianData.phone"
            name="newLibrarianPhone"
            label="电话"
            type="textarea"
            rows="2"
            autosize
            placeholder="请输入电话，多个可用逗号等分隔"
            clearable
          />
        </van-cell-group>
      </van-form>
    </van-dialog>

  </div>
</template>

<script setup>
// --- 导入 ---
import { ref, onMounted, computed, watch, reactive, h } from 'vue';
import { useRouter } from 'vue-router';
import { 
  getLibrarians, 
  getLibrarianContactLogs, 
  updateContactLog, 
  addContactLog, 
  importLibrarians, 
  createLibrarian,
  getUsers,
  createUser,
  updateUser,
  changePassword,
  deleteUser,
  exportContactLogs
} from '../services/api';
import {
  showNotify,
  Sticky,
  showDialog, // 用于删除用户时的密码输入
  Field as VanField, // 在 showDialog 的 message 函数中创建 van-field 时需要
  // Dialog as VanDialog, // 如果其他地方没有用 Dialog.confirm, 可以移除这个别名导入
  // 其他 Vant 组件如 Uploader, Popup 等依赖全局注册
} from 'vant';
import ContactModal from '../components/ContactModal.vue';
import { pinyin } from 'pinyin'; // 确保已安装: npm install pinyin

// --- 核心状态与路由 ---
const router = useRouter();
const showDevMessages = ref(false); // 控制开发调试日志的显示
// // 只在开发环境显示详细日志，取消注释下一行来启用
// if (process.env.NODE_ENV === 'development') { showDevMessages.value = true; }
if (showDevMessages.value) console.log('LibrarianList.vue: Script setup executing');

// --- 馆员列表相关状态 ---
const allLibrarians = ref([]);
const loadingLibrarians = ref(false);
const componentInitialized = ref(false);
const initialLoadingError = ref(false);

// --- 弹窗状态 ---
const showContactModal = ref(false);
const selectedLibrarian = ref(null); // 用于"记录联系"和"编辑"时传递数据
const editingContactLogData = ref(null); // 用于传递给 ContactModal 进行编辑

const showAdminPanel = ref(false);
const showExportDialog = ref(false);
const showStartDatePicker = ref(false);
const showEndDatePicker = ref(false);
const showCreateUserDialog = ref(false);
const showEditUserDialog = ref(false);
const showChangePasswordDialog = ref(false);
const showAddLibrarianDialog = ref(false); // 添加馆员弹窗显示状态

// --- 数据状态 ---
const exportStartDate = ref('');
const exportEndDate = ref('');
const minDateForPicker = new Date(new Date().getFullYear() - 1, 0, 1); // 上一年的1月1日
const maxDateForPicker = new Date();

// 展开详情相关
const expandedLibrarianId = ref(null);
const currentExpandedLogs = ref([]);
const loadingExpandedLogs = ref(false);

// 搜索相关
const searchKeyword = ref('');

// 用户管理相关
const activeAdminTab = ref(0); // 0 for 数据管理, 1 for 用户管理
const users = ref([]);
const loadingUsers = ref(false);
const isUploading = ref(false); // 导入文件加载状态
const isDeletingUser = ref(null); // 正在删除的用户ID
const currentUserId = ref(null); // 当前登录用户的ID

// 创建新用户表单相关
const createUserFormRef = ref(null);
const newUserData = reactive({ username: '', full_name: '', password: '', confirmPassword: '' });
const isSubmittingUserForm = ref(false); // 用于创建、编辑、改密、添加馆员表单的提交加载状态

// 编辑用户表单相关
const editUserFormRef = ref(null);
const editingUserData = reactive({ id: null, username: '', full_name: '' });

// 修改密码表单相关
const changePasswordFormRef = ref(null);
const userForPasswordChange = reactive({ id: null, username: '' });
const changePasswordData = reactive({ old_password: '', new_password: '', confirm_new_password: '' });
const isEditingSelfPassword = ref(false);

// 添加新馆员表单相关
const addLibrarianFormRef = ref(null);
const newLibrarianData = reactive({ name: '', birthday: '', phone: '' });
const isSubmittingLibrarianForm = ref(false); // 可以与 isSubmittingUserForm 复用，或单独定义


// --- 辅助函数 ---
const getPinyinInitials = (text) => {
  if (!text) return '';
  return pinyin(text, { style: pinyin.STYLE_FIRST_LETTER })
    .map(arr => arr[0] || '')
    .join('')
    .toLowerCase();
};
const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};
const formatDate = (dateObj) => {
  if (!dateObj) return '待定';
  const d = dateObj instanceof Date ? dateObj : new Date(dateObj);
  if (isNaN(d.getTime())) return '日期无效';
  const year = d.getFullYear();
  const month = ('0' + (d.getMonth() + 1)).slice(-2);
  const day = ('0' + d.getDate()).slice(-2);
  return `${year}-${month}-${day}`;
};
const formatTimestamp = (timestamp, showSeconds = false) => {
  if (!timestamp) return null;
  
  let date;
  
  // 检查是否为数字（Unix时间戳）或字符串（SQLite TIMESTAMP）
  if (typeof timestamp === 'number' || !isNaN(Number(timestamp))) {
    // 处理Unix时间戳（秒）
    date = new Date(Number(timestamp) * 1000);
  } else if (typeof timestamp === 'string') {
    // 处理SQLite TIMESTAMP字符串
    try {
      date = new Date(timestamp + 'Z'); // 添加Z表示这是UTC时间
    } catch (e) {
      console.error('Invalid timestamp format:', timestamp);
      return 'Invalid date';
    }
  } else {
    console.error('Unknown timestamp format:', timestamp);
    return 'Unknown format';
  }
  
  // 检查日期是否有效
  if (isNaN(date.getTime())) {
    console.error('Invalid date object from timestamp:', timestamp);
    return 'Invalid date';
  }
  
  // 获取UTC时间的各个部分
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  
  // 计算北京时间（UTC+8）的小时
  let hour = date.getUTCHours() + 8;
  let dayOffset = 0;
  
  // 处理跨天的情况
  if (hour >= 24) {
    hour -= 24;
    dayOffset = 1;
  }
  
  const minute = String(date.getUTCMinutes()).padStart(2, '0');
  
  // 如果跨天，需要调整日期
  let finalDay = parseInt(day, 10) + dayOffset;
  let finalMonth = parseInt(month, 10);
  let finalYear = year;
  
  // 处理月末跨天
  const daysInMonth = new Date(Date.UTC(year, finalMonth, 0)).getUTCDate();
  if (finalDay > daysInMonth) {
    finalDay = 1;
    finalMonth += 1;
    
    // 处理年末跨天
    if (finalMonth > 12) {
      finalMonth = 1;
      finalYear += 1;
    }
  }
  
  const formattedDay = String(finalDay).padStart(2, '0');
  const formattedMonth = String(finalMonth).padStart(2, '0');
  const formattedHour = String(hour).padStart(2, '0');
  
  let result = `${finalYear}-${formattedMonth}-${formattedDay} ${formattedHour}:${minute}`;
  
  if (showSeconds) {
    const second = String(date.getUTCSeconds()).padStart(2, '0');
    result += `:${second}`;
  }
  
  return result;
};
const getHealthStatusColor = (status) => {
  if (status === '健康') return 'green';
  if (status === '生病') return 'red';
  return '#646566'; // 默认或未记录
};

// --- 计算属性: sortedLibrarians ---
const sortedLibrarians = computed(() => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = addDays(new Date(today), 1);
  let filteredLibrarians = allLibrarians.value;
  if (searchKeyword.value.trim() !== '') {
    const keyword = searchKeyword.value.trim().toLowerCase();
    filteredLibrarians = allLibrarians.value.filter(lib => {
      const nameMatch = lib.name.toLowerCase().includes(keyword);
      const pinyinInitials = getPinyinInitials(lib.name);
      const pinyinMatch = pinyinInitials.includes(keyword);
      return nameMatch || pinyinMatch;
    });
  }
  const processedLibrarians = filteredLibrarians.map(lib => {
    let effectiveNextContactDate = null, nameColor = 'black', isDueForFollowUpFromSickness = false, sortGroup = 1;
    if (!lib.last_contact_timestamp) { nameColor = 'black'; sortGroup = 0; }
    else {
      const lastContactDate = new Date(lib.last_contact_timestamp * 1000); sortGroup = 1;
      if (lib.health_status === '生病') {
        let followUpDate;
        if (lib.follow_up_date) { try { const [year, month, day] = lib.follow_up_date.split('-').map(Number); followUpDate = new Date(year, month - 1, day); if (isNaN(followUpDate.getTime())) followUpDate = null; } catch(e) { followUpDate = null; if (showDevMessages.value) console.warn(`Invalid follow_up_date for ${lib.name}: ${lib.follow_up_date}`);} }
        if (!followUpDate || isNaN(followUpDate.getTime())) { followUpDate = addDays(new Date(lastContactDate), 15); }
        effectiveNextContactDate = followUpDate;
        const effectiveNextContactDayStart = new Date(effectiveNextContactDate); effectiveNextContactDayStart.setHours(0,0,0,0);
        if (effectiveNextContactDayStart <= today) { nameColor = 'red'; isDueForFollowUpFromSickness = true; } else { nameColor = 'green'; }
      } else { effectiveNextContactDate = addDays(new Date(lastContactDate), 30); nameColor = 'green'; }
      const effectiveNextContactDayStartForCompare = effectiveNextContactDate ? new Date(effectiveNextContactDate) : null;
      if (effectiveNextContactDayStartForCompare) { effectiveNextContactDayStartForCompare.setHours(0,0,0,0); }
      const sevenDaysPastNextContact = effectiveNextContactDayStartForCompare ? addDays(new Date(effectiveNextContactDayStartForCompare), 7) : null;
      if (sevenDaysPastNextContact && today >= sevenDaysPastNextContact) { if (nameColor !== 'red') { nameColor = 'black'; } }
      else if (nameColor !== 'red' && nameColor !== 'black' && effectiveNextContactDayStartForCompare) {
        if (effectiveNextContactDayStartForCompare <= tomorrow && effectiveNextContactDayStartForCompare >= today) { nameColor = 'blue'; }
      }
    }
    return { ...lib, displayNextContactDate: effectiveNextContactDate ? formatDate(effectiveNextContactDate) : (lib.last_contact_timestamp ? '待计算' : '从未联系'), nameColor, isDueForFollowUpFromSickness, sortGroup, sortDate: effectiveNextContactDate ? effectiveNextContactDate.getTime() : (sortGroup === 0 ? -Infinity : Infinity), sortName: lib.name, };
  });
  return processedLibrarians.sort((a, b) => {
    if (a.sortGroup !== b.sortGroup) { return a.sortGroup - b.sortGroup; }
    if (a.sortGroup === 0) { return a.sortName.localeCompare(b.sortName, 'zh-CN'); }
    if (a.nameColor === 'red' && b.nameColor !== 'red') return -1; if (a.nameColor !== 'red' && b.nameColor === 'red') return 1;
    if (a.nameColor === 'blue' && b.nameColor !== 'blue') return -1; if (a.nameColor !== 'blue' && b.nameColor === 'blue') return 1;
    if (a.nameColor === 'black' && b.nameColor !== 'black' && a.sortGroup === 1 && b.sortGroup === 1) return -1;
    if (a.nameColor !== 'black' && b.nameColor === 'black' && a.sortGroup === 1 && b.sortGroup === 1) return 1;
    if (a.sortDate !== b.sortDate) { return a.sortDate - b.sortDate; }
    return a.sortName.localeCompare(b.sortName, 'zh-CN');
  });
});

// --- 方法 ---

// ---- 数据加载 ----
const onLoadLibrarians = async () => {
  if (showDevMessages.value) console.log('LibrarianList.vue: onLoadLibrarians - START');
  loadingLibrarians.value = true; initialLoadingError.value = false;
  try {
    const response = await getLibrarians();
    if (response && response.data && Array.isArray(response.data)) { allLibrarians.value = response.data; }
    else { allLibrarians.value = []; showNotify({ type: 'danger', message: '加载馆员数据格式错误' }); initialLoadingError.value = true; }
  } catch (error) {
    allLibrarians.value = []; let errMsg = error.response?.data?.message || error.message || '未知错误';
    showNotify({ type: 'danger', message: `加载馆员列表失败: ${errMsg}` }); initialLoadingError.value = true;
  } finally { loadingLibrarians.value = false; componentInitialized.value = true; if (showDevMessages.value) console.log('LibrarianList.vue: onLoadLibrarians - END'); }
};

// ---- 用户交互 ----
const onSearchInput = (value) => { if (showDevMessages.value) console.log('Search keyword:', value); };
const toggleDetails = async (librarianId) => { 
  if (expandedLibrarianId.value === librarianId) { 
    expandedLibrarianId.value = null; 
    currentExpandedLogs.value = []; 
  } else { 
    expandedLibrarianId.value = librarianId; 
    currentExpandedLogs.value = []; 
    loadingExpandedLogs.value = true; 
    if (showDevMessages.value) console.log('LibrarianList.vue: toggleDetails - 开始获取联系记录，librarianId:', librarianId);
    try { 
      const response = await getLibrarianContactLogs(librarianId); 
      if (showDevMessages.value) console.log('LibrarianList.vue: toggleDetails - 获取联系记录响应:', response);
      if (response && response.data && Array.isArray(response.data)) { 
        currentExpandedLogs.value = response.data; 
      } else { 
        currentExpandedLogs.value = []; 
        showNotify({ type: 'warning', message: '获取联系记录失败或记录为空' }); 
      } 
    } catch (error) { 
      if (showDevMessages.value) console.error('LibrarianList.vue: toggleDetails - 获取联系记录错误:', error);
      currentExpandedLogs.value = []; 
      showNotify({ type: 'danger', message: `获取联系记录失败: ${error.message || '未知错误'}` }); 
    } finally { 
      loadingExpandedLogs.value = false; 
    } 
  } 
};
const openContactModal = (librarian, isEditMode = false, logIdToEdit = null) => {
  selectedLibrarian.value = { ...librarian };
  if (isEditMode && logIdToEdit) {
    editingContactLogData.value = {
      id: logIdToEdit,
      call_duration_minutes: librarian.last_call_duration,
      notes: librarian.last_contact_notes,
      health_status_recorded: librarian.last_health_status_recorded,
      contacted_phone_number: librarian.last_contacted_phone_number
    };
    if (showDevMessages.value) console.log("Opening ContactModal in EDIT mode for log:", editingContactLogData.value);
  } else {
    editingContactLogData.value = null;
    if (showDevMessages.value) console.log("Opening ContactModal in CREATE mode");
  }
  showContactModal.value = true;
};
const onContactModalClosed = () => { editingContactLogData.value = null; };
const handleContactSubmit = async (formDataFromModal) => { if (showDevMessages.value) console.log('LibrarianList.vue: handleContactSubmit CALLED with formData:', formDataFromModal); try { let response; if (editingContactLogData.value && editingContactLogData.value.id) { const logId = editingContactLogData.value.id; const updateData = { call_duration_minutes: formDataFromModal.call_duration_minutes, notes: formDataFromModal.notes, health_status_recorded: formDataFromModal.health_status_recorded, }; if (showDevMessages.value) console.log(`Submitting UPDATE to /api/contacts/${logId}`, updateData); response = await updateContactLog(logId, updateData); } else { if (showDevMessages.value) console.log('Submitting CREATE to /api/contacts', formDataFromModal); response = await addContactLog(formDataFromModal); } if (response && (response.status === 200 || response.status === 201)) { const successMessage = editingContactLogData.value ? '联系记录更新成功' : (response.data.message || '联系记录已保存'); showNotify({ type: 'success', message: successMessage }); showContactModal.value = false; onLoadLibrarians(); if (expandedLibrarianId.value === selectedLibrarian.value?.id) { const currentExpandedId = expandedLibrarianId.value; expandedLibrarianId.value = null; currentExpandedLogs.value = []; await toggleDetails(currentExpandedId); } } else { showNotify({ type: 'danger', message: response?.data?.message || (editingContactLogData.value ? '更新失败' : '保存失败') + '，响应异常' }); } } catch (error) { let errMsg = error.response?.data?.message || error.message || '未知错误'; showNotify({ type: 'danger', message: (editingContactLogData.value ? '更新失败' : '保存失败') + ': ' + errMsg }); } finally { editingContactLogData.value = null; } };
const editLibrarian = (id) => { if (id) { router.push({ name: 'LibrarianEdit', params: { id: id } }); } else { showNotify({ type: 'warning', message: '无效的馆员ID' }); } };

// ---- 管理面板数据管理 ----
const handleImport = async (file) => { if (showDevMessages.value) console.log('LibrarianList.vue: handleImport - Vant Uploader file object:', file); if (!file || !file.file) { showNotify({ type: 'danger', message: '未选择文件或文件对象无效' }); return; } isUploading.value = true; const formData = new FormData(); formData.append('file', file.file); try { const response = await importLibrarians(formData); if (response && response.data && typeof response.data.message === 'string') { showNotify({ type: (response.status === 200 || response.status === 201) ? 'success' : 'warning', message: response.data.message }); if (response.status === 200 || response.status === 201) { componentInitialized.value = false; onLoadLibrarians(); showAdminPanel.value = false; } } else { showNotify({ type: 'danger', message: '导入操作完成，但服务器响应格式不正确。' }); } } catch (error) { let errMsg = error.response?.data?.message || error.message || '未知错误'; showNotify({ type: 'danger', message: '导入失败: ' + errMsg }); } finally { isUploading.value = false; } };
const openExportDialogInAdminPanel = () => { showAdminPanel.value = false; showExportDialog.value = true; };
const onStartDateConfirm = (value) => {
  let arr = Array.isArray(value) ? value : value?.selectedValues;
  if (arr && arr.length === 3) {
    exportStartDate.value = `${arr[0]}-${arr[1].padStart(2, '0')}-${arr[2].padStart(2, '0')}`;
  }
  showStartDatePicker.value = false;
};
const onEndDateConfirm = (value) => {
  let arr = Array.isArray(value) ? value : value?.selectedValues;
  if (arr && arr.length === 3) {
    exportEndDate.value = `${arr[0]}-${arr[1].padStart(2, '0')}-${arr[2].padStart(2, '0')}`;
  }
  showEndDatePicker.value = false;
};
const handleExport = async () => {
  try {
    const response = await exportContactLogs({
      start_date: exportStartDate.value,
      end_date: exportEndDate.value
    });
    
    // 创建下载链接
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contact_logs_${exportStartDate.value}_to_${exportEndDate.value}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    showNotify({ type: 'success', message: '导出成功' });
  } catch (error) {
    console.error('导出失败:', error);
    showNotify({ type: 'danger', message: '导出失败: ' + (error.response?.data?.message || error.message || '未知错误') });
  }
};
const resetAddLibrarianForm = () => { newLibrarianData.name = ''; newLibrarianData.birthday = ''; newLibrarianData.phone = ''; if (addLibrarianFormRef.value) { addLibrarianFormRef.value.resetValidation(); } };
const openAddLibrarianDialog = () => { resetAddLibrarianForm(); showAddLibrarianDialog.value = true; };
const handleAddLibrarian = async () => { isSubmittingLibrarianForm.value = true; try { const librarianDataToSubmit = { name: newLibrarianData.name, birthday: newLibrarianData.birthday, phone: newLibrarianData.phone, }; const response = await createLibrarian(librarianDataToSubmit); if (response && (response.status === 201 || response.status === 200)) { showNotify({ type: 'success', message: response.data.message || '馆员添加成功' }); showAddLibrarianDialog.value = false; onLoadLibrarians(); showAdminPanel.value = false; } else { showNotify({ type: 'danger', message: response?.data?.message || '添加馆员失败' }); } } catch (error) { showNotify({ type: 'danger', message: `添加馆员API错误: ${error.response?.data?.message || error.message || '未知API错误'}` }); } finally { isSubmittingLibrarianForm.value = false; } };
const beforeAddLibrarianDialogClose = async (action) => { if (action === 'confirm') { if (isSubmittingLibrarianForm.value) return false; try { await addLibrarianFormRef.value.validate(); return true; } catch (errors) { if (Array.isArray(errors) && errors.length > 0 && errors[0].message) { showNotify({ type: 'warning', message: errors[0].message }); } else { showNotify({ type: 'warning', message: '请检查表单填写是否正确' }); } return false; } } return true; };

// ---- 用户管理方法 ----
const fetchUsers = async () => { loadingUsers.value = true; try { const response = await getUsers(); if (response && response.data && Array.isArray(response.data)) { users.value = response.data; } else { users.value = []; showNotify({ type: 'danger', message: '获取用户列表失败或数据格式不正确' }); } } catch (error) { users.value = []; showNotify({ type: 'danger', message: `获取用户列表错误: ${error.response?.data?.message || error.message || '未知错误'}` }); } finally { loadingUsers.value = false; } };
const onAdminPanelOpen = () => { if (showDevMessages.value) console.log("Admin panel opened. Active tab:", activeAdminTab.value); if (activeAdminTab.value === 1 && showAdminPanel.value) { fetchUsers(); } };
watch(activeAdminTab, (newTab) => { if (showDevMessages.value) console.log(`Admin tab changed to ${newTab}`); if (newTab === 1 && showAdminPanel.value) { fetchUsers(); } });
const resetCreateUserForm = () => { newUserData.username = ''; newUserData.full_name = ''; newUserData.password = ''; newUserData.confirmPassword = ''; if (createUserFormRef.value) { createUserFormRef.value.resetValidation(); } };
const openCreateUserDialog = () => { resetCreateUserForm(); showCreateUserDialog.value = true; };
const passwordValidator = (val) => val && val.length >= 6;
const confirmPasswordValidatorForCreate = (val) => val === newUserData.password;
const handleCreateUser = async () => { isSubmittingUserForm.value = true; try { const userDataToSubmit = { username: newUserData.username, password: newUserData.password, full_name: newUserData.full_name, }; const response = await createUser(userDataToSubmit); if (response && (response.status === 201 || response.status === 200)) { showNotify({ type: 'success', message: response.data.message || '用户创建成功' }); showCreateUserDialog.value = false; fetchUsers(); } else { showNotify({ type: 'danger', message: response?.data?.message || '创建用户失败' }); } } catch (error) { showNotify({ type: 'danger', message: `创建用户API错误: ${error.response?.data?.message || error.message || '未知API错误'}` }); } finally { isSubmittingUserForm.value = false; } };
const beforeCreateUserDialogClose = async (action) => { if (action === 'confirm') { if (isSubmittingUserForm.value) return false; try { await createUserFormRef.value.validate(); return true; } catch (errors) { if (Array.isArray(errors) && errors.length > 0 && errors[0].message) { showNotify({ type: 'warning', message: errors[0].message }); } else { showNotify({ type: 'warning', message: '请检查表单填写是否正确' }); } return false; } } return true; };
const resetEditUserForm = () => { editingUserData.id = null; editingUserData.username = ''; editingUserData.full_name = ''; if (editUserFormRef.value) { editUserFormRef.value.resetValidation(); }};
const openEditUserDialog = (user) => { editingUserData.id = user.id; editingUserData.username = user.username; editingUserData.full_name = user.full_name || ''; showEditUserDialog.value = true; };
const handleEditUser = async () => { isSubmittingUserForm.value = true; try { const userDataToUpdate = { username: editingUserData.username, full_name: editingUserData.full_name, }; const response = await updateUser(editingUserData.id, userDataToUpdate); if (response && response.status === 200) { showNotify({ type: 'success', message: response.data.message || '用户信息更新成功' }); showEditUserDialog.value = false; fetchUsers(); } else { showNotify({ type: 'danger', message: response?.data?.message || '更新用户信息失败' }); } } catch (error) { showNotify({ type: 'danger', message: `更新用户API错误: ${error.response?.data?.message || error.message || '未知API错误'}` }); } finally { isSubmittingUserForm.value = false; } };
const beforeEditUserDialogClose = async (action) => { if (action === 'confirm') { if (isSubmittingUserForm.value) return false; try { await editUserFormRef.value.validate(); return true; } catch (errors) { if (Array.isArray(errors) && errors.length > 0 && errors[0].message) { showNotify({ type: 'warning', message: errors[0].message }); } else { showNotify({ type: 'warning', message: '请检查表单填写是否正确' }); } return false; } } return true; };
const openChangePasswordDialog = (user) => { userForPasswordChange.id = user.id; userForPasswordChange.username = user.username; isEditingSelfPassword.value = (user.id === currentUserId.value); changePasswordData.old_password = ''; changePasswordData.new_password = ''; changePasswordData.confirm_new_password = ''; if (changePasswordFormRef.value) { changePasswordFormRef.value.resetValidation(); } showChangePasswordDialog.value = true; };
const resetChangePasswordForm = () => { changePasswordData.old_password = ''; changePasswordData.new_password = ''; changePasswordData.confirm_new_password = ''; if (changePasswordFormRef.value) { changePasswordFormRef.value.resetValidation(); }};
const confirmPasswordValidatorForChange = (val) => val === changePasswordData.new_password;
const handleChangePassword = async () => { isSubmittingUserForm.value = true; try { const payload = { new_password: changePasswordData.new_password, }; if (isEditingSelfPassword.value) { payload.old_password = changePasswordData.old_password; } const response = await changePassword(userForPasswordChange.id, payload); if (response && response.status === 200) { showNotify({ type: 'success', message: response.data.message || '密码修改成功' }); showChangePasswordDialog.value = false; if (isEditingSelfPassword.value) { showNotify({ type: 'info', message: '您的密码已更新，建议重新登录。', duration: 3000 }); } } else { showNotify({ type: 'danger', message: response?.data?.message || '修改密码失败' }); } } catch (error) { showNotify({ type: 'danger', message: `修改密码API错误: ${error.response?.data?.message || error.message || '未知API错误'}` }); } finally { isSubmittingUserForm.value = false; } };
const beforeChangePasswordDialogClose = async (action) => { if (action === 'confirm') { if (isSubmittingUserForm.value) return false; try { await changePasswordFormRef.value.validate(); return true; } catch (errors) { if (Array.isArray(errors) && errors.length > 0 && errors[0].message) { showNotify({ type: 'warning', message: errors[0].message }); } else { showNotify({ type: 'warning', message: '请检查表单填写是否正确' }); } return false; } } return true; };
const confirmDeleteUser = async (userToDelete) => { if (isDeletingUser.value) return; if (currentUserId.value === userToDelete.id) { showNotify({type: 'warning', message: '不能删除当前登录用户'}); return; } const passwordInput = ref(''); try { await showDialog({ title: '删除用户确认', message: () => h('div', { style: { padding: '10px 0' } }, [ h('p', { style: { marginBottom: '10px', fontSize: 'var(--van-font-size-md)' } }, `请输入您的登录密码以删除用户 "${userToDelete.username}"：`), h(VanField, { modelValue: passwordInput.value, 'onUpdate:modelValue': (val) => { passwordInput.value = val; }, type: 'password', placeholder: '请输入您的密码', rules: [{ required: true, message: '密码不能为空' }], }) ]), showCancelButton: true, confirmButtonText: '确认删除', beforeClose: async (action) => { if (action === 'confirm') { if (!passwordInput.value || passwordInput.value.trim() === '') { showNotify({ type: 'warning', message: '请输入密码以确认删除' }); return false; } return true; } return true; } }); const operatorPassword = passwordInput.value; isDeletingUser.value = userToDelete.id; await deleteUser(userToDelete.id, { password: operatorPassword }); showNotify({ type: 'success', message: `用户 "${userToDelete.username}" 已删除` }); fetchUsers(); } catch (actionOrError) { if (actionOrError === 'cancel') { console.log('User deletion cancelled by user.'); } else if (actionOrError) { showNotify({ type: 'danger', message: `删除用户失败: ${actionOrError.response?.data?.message || actionOrError.message || '未知错误'}` }); } } finally { isDeletingUser.value = null; } };

// --- 生命周期钩子 ---
onMounted(() => {
  if (showDevMessages.value) console.log('LibrarianList.vue: onMounted hook called');
  onLoadLibrarians();
  const token = localStorage.getItem('token');
  if (token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decodedToken = JSON.parse(jsonPayload);
        currentUserId.value = decodedToken.userId;
        if (showDevMessages.value) {
            console.log('Decoded JWT payload:', decodedToken);
            console.log('Current user ID:', currentUserId.value);
        }
    } catch (e) {
        console.error("Failed to parse userId from token on mount:", e);
        currentUserId.value = null;
    }
  }

  // 设置默认日期范围（上一年的1月1日到当天）
  const today = new Date();
  const lastYear = new Date(today.getFullYear() - 1, 0, 1); // 上一年的1月1日
  exportStartDate.value = formatDateForExport(lastYear);
  exportEndDate.value = formatDateForExport(today);
});

// 格式化日期为YYYY-MM-DD
const formatDateForExport = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
</script>

<style scoped>
/* 所有样式与上一版本一致 */
.librarian-list-page { display: flex; flex-direction: column; height: 100vh; }
.sticky-header-content { background-color: #fff; z-index: 10; position: relative; }
.top-bar-with-search { display: flex; align-items: center; padding: 8px var(--van-padding-md); }
.inline-search-field { flex-grow: 1; margin-right: 10px; }
.inline-search-field .van-field__control { font-size: var(--van-font-size-md); }
.inline-manage-button { white-space: nowrap; flex-shrink: 0; font-size: var(--van-font-size-sm); padding: 0 10px; height: 36px; }
.scrollable-list-area { flex-grow: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; position: relative; }
.admin-panel { padding: 0 0 16px 0; min-height: 300px; }
.admin-panel .van-tabs__content { padding: 10px; }
.admin-cell-group { margin-top: 15px !important; }
.admin-cell-group .van-cell__title { font-weight: 500; }
.user-management-content { padding: 10px; }
.user-actions { display: flex; flex-direction: column; align-items: flex-end; }
.user-actions .van-button { margin-bottom: 5px; }
.user-actions .van-button:last-child { margin-bottom: 0; }
.user-title { font-weight: 500; font-size: var(--van-font-size-md); }
.user-label { font-size: var(--van-font-size-sm); color: #969799; margin-top: 2px; }
.user-form-dialog .van-dialog__content { max-height: 60vh; overflow-y: auto; }
.user-form-padding { padding: 20px 10px 0; }
.actions-bar { display: flex; justify-content: flex-end; padding: 0 var(--van-padding-md) 10px; background-color: #fff; }
.action-bar-button { font-weight: 500; }
.librarian-van-list .van-cell { padding-top: var(--van-cell-vertical-padding); padding-bottom: var(--van-cell-vertical-padding); }
.librarian-name { font-weight: bold; font-size: var(--van-font-size-lg); cursor: pointer; display: inline-flex; align-items: center; }
.librarian-detail { margin-top: 6px; color: #646566; font-size: var(--van-font-size-sm); }
.follow-up-date { color: var(--van-danger-color); font-weight: 500; }
.item-actions { display: flex; flex-direction: column; align-items: flex-end; justify-content: center; }
.action-button { margin-top: 5px; min-width: 70px; }
.action-button:first-child { margin-top: 0; }
.edit-button { margin-left: 0; }
.librarian-details-expanded { padding: 0 var(--van-padding-sm) var(--van-padding-sm) var(--van-padding-sm); background-color: #fdfdfd; border-top: 1px solid #f0f0f0; border-bottom: 1px solid #f0f0f0; margin-bottom: 8px; max-height: 300px; overflow-y: auto; }
.expanded-details-group { margin: 0; }
.expanded-details-group .van-cell { padding-top: 10px; padding-bottom: 10px; background-color: transparent; }
.expanded-details-group .van-cell__value { text-align: left; color: #323233; font-size: var(--van-font-size-sm); }
.expanded-details-group .van-cell__title { flex: 0 0 110px; font-size: var(--van-font-size-sm); color: #646566; }
.log-item { border-bottom: 1px dashed #e0e0e0; padding-bottom: 8px; margin-bottom: 8px; }
.log-item:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.log-notes .van-cell__value { white-space: pre-wrap; word-break: break-all; }
.expanded-loading, .expanded-no-logs { padding: 20px; text-align: center; color: #969799; font-size: var(--van-font-size-sm); }

.modify-info-button {
  margin-left: auto; /* 尝试将"修改信息"按钮推到标题行右侧 */
  /* margin-left: 8px; */ /* 或者给一个固定的小左边距 */
  padding: 2px 6px !important; /* 确保内边距小一点 */
  height: 24px !important; /* 确保按钮小一点 */
  line-height: 20px !important; /* 调整行高使文字居中 */
  font-size: 12px !important; /* 字体也小一点 */
  border-radius: 4px;
  flex-shrink: 0; /* 防止被压缩 */
}
.librarian-title-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.item-actions .edit-record-button { /* 编辑最近记录按钮的样式 */
    margin-top: 5px; /* 与记录联系按钮的间距 */
}

</style>